<template>
  <div class="nosp-inventory-container">
    <h1>NOSP Inventory Dashboard</h1>
    
    <div class="filter-container">
      <div class="sales-unit-tabs">
        <button 
          v-for="unit in salesUnits" 
          :key="unit.value"
          :class="['tab-button', { active: salesUnit === unit.value }]"
          @click="salesUnit = unit.value; fetchInventoryData()"
        >
          {{ unit.label }}
        </button>
      </div>
      
      <div class="week-selector">
        <div class="week-buttons">
          <button 
            v-for="w in weekOptions" 
            :key="w"
            :class="['week-button', { active: week === w }]"
            @click="week = w; fetchInventoryData()"
          >
            {{ getWeekLabel(w) }}
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
    </div>
    
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>
    
    <div v-else class="timetable-container" style="margin-bottom: 50px;">
      <table class="timetable">
        <thead>
          <tr>
            <th>시간</th>
            <th v-for="(day, index) in weekDays" :key="index">{{ day.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr class="all-day-row">
            <td>all-day</td>
            <td v-for="(day, dayIndex) in weekDays" :key="`all-day-${dayIndex}`" :class="{ 'today-column': isToday(day.date) }"></td>
          </tr>
          <tr v-for="hour in 24" :key="`hour-${hour-1}`">
            <td>{{ formatHour(hour-1) }}</td>
            <td v-for="(day, dayIndex) in weekDays" :key="`${hour-1}-${dayIndex}`" class="time-slot" :class="{ 'today-column': isToday(day.date) }">
              <div 
                v-for="(item, itemIndex) in getInventoryItemsForTimeSlot(day.date, hour-1)" 
                :key="`item-${itemIndex}`"
                class="inventory-item"
                :style="getItemStyle(day.date, hour-1)"
                @mouseenter="showTooltip($event, item)"
                @mouseleave="hideTooltip"
              >
                {{ getItemContent(item) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- 디버깅용 JSON 데이터 표시 영역 추가 -->
    <!--
    <div class="debug-container">
      <h3>디버깅 데이터 (백엔드 응답)</h3>
      <div class="toggle-button" @click="toggleRawData">
        {{ showRawData ? '숨기기' : '보기' }}
      </div>
      <pre v-if="showRawData" class="raw-data">{{ rawData }}</pre>
    </div>
    -->
    
    <!-- 말풍선 컴포넌트 -->
    <div v-if="tooltip.visible" 
         class="tooltip"
         :style="tooltip.style">
      {{ tooltip.content }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';

export default {
  name: 'NOSPInventory',
  setup() {
    const inventoryData = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const salesUnit = ref('da');
    const week = ref(0); // Changed default to 0 for current week
    const rawData = ref(null); // 백엔드 원본 데이터 저장
    const showRawData = ref(false); // 원본 데이터 표시 여부
    
    // 현재 날짜 정보 계산
    const today = new Date();
    const currentWeekday = today.getDay(); // 0: 일요일, 6: 토요일
    const mondayOffset = currentWeekday === 0 ? -6 : 1 - currentWeekday; // 월요일까지의 오프셋
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() + mondayOffset);
    
    // 요일 정보 계산
    const weekDays = computed(() => {
      const days = [];
      const startDate = new Date(currentMonday);
      
      // 선택된 주차에 따라 시작일 조정
      if (week.value > 0) {
        startDate.setDate(startDate.getDate() + (7 * week.value));
      }
      
      // 월요일부터 일요일까지의 날짜 생성
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        
        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const dayName = dayNames[date.getDay()];
        
        days.push({
          date: formatDate(date),
          label: `${dayName} ${month}.${day}`,
          dayOfWeek: date.getDay()
        });
      }
      
      return days;
    });
    
    // 날짜 포맷 함수 (YYYYMMDD)
    function formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    // 시간 포맷 함수 (HH시)
    function formatHour(hour) {
      return `${hour < 10 ? '0' : ''}${hour}시`;
    }
    
    // 오늘 날짜 확인 함수
    const isToday = (date) => {
      const today = new Date();
      const todayFormatted = formatDate(today);
      return date === todayFormatted;
    };
    
    // 인벤토리 데이터 가져오기
    const fetchInventoryData = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        // 백엔드 서버 연결 시도
        try {
          // 백엔드 서버 상태 확인
          console.log('백엔드 서버 연결 시도:', `/nosp/inventory?sales_unit=${salesUnit.value}&week=${week.value}`);
          
          const response = await axios.get('/nosp/inventory', {
            params: {
              sales_unit: salesUnit.value,
              week: week.value
            },
            timeout: 3000 // 3초 타임아웃 설정
          });
          
          // 백엔드 원본 데이터 저장
          rawData.value = response.data;
          console.log('백엔드 응답 성공:', response.data);
          
          // 백엔드 응답 데이터를 UI에 맞게 변환
          inventoryData.value = processInventoryData(response.data);
        } catch (apiError) {
          console.error('API 호출 오류 상세 정보:', apiError.message, apiError.response || '응답 없음');
        }
      } catch (err) {
        console.error('인벤토리 데이터 로딩 오류:', err);
        error.value = '인벤토리 데이터를 불러오는데 실패했습니다. 나중에 다시 시도해주세요.';
      } finally {
        loading.value = false;
      }
    };

    // 원본 데이터 표시 토글
    const toggleRawData = () => {
      showRawData.value = !showRawData.value;
    };
    
    // 백엔드 데이터를 UI에 맞게 처리
    const processInventoryData = (data) => {
      const processed = [];
      
      if (data && data.list && Array.isArray(data.list)) {
        console.log('Processing data for dates:', weekDays.value.map(d => d.date)); // 디버깅용 로그
        
        data.list.forEach(item => {
          // 시작 시간과 종료 시간 파싱
          const startDate = new Date(item.start);
          const endDate = new Date(item.end);
          
          const startHour = startDate.getHours();
          const endHour = endDate.getHours();
          
          const processedItem = {
            date: item.ymd,
            sale_unit_id: item.saleUnitId,
            title: item.title,
            description: item.description,
            time_range: `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:59`,
            startHour,
            endHour,
            duration: endHour - startHour + 1,
            campId: item.campId,
            id: item.id
          };
          
          console.log('Processed item:', processedItem); // 디버깅용 로그
          processed.push(processedItem);
        });
      }
      
      return processed;
    };
    
    // 특정 날짜와 시간에 해당하는 인벤토리 항목 가져오기
    const getInventoryItemsForTimeSlot = (date, hour) => {
      return inventoryData.value.filter(item => 
        item.date === date && 
        hour >= item.startHour && 
        hour <= item.endHour
      );
    };
    
    // 인벤토리 항목의 스타일 계산
    const getItemStyle = (date, hour) => {
      const items = getInventoryItemsForTimeSlot(date, hour);
      const height = items.length === 1 ? '90%' : '20px';
      
      return {
        backgroundColor: '#5b9bd5',
        color: 'white',
        height: height,
        padding: '1px',
        fontSize: '12px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      };
    };
    
    // 인벤토리 항목의 내용 표시
    const getItemContent = (item) => {
      return `${item.time_range} - ${item.title}`;
    };
    
    // 말풍선 내용 표시
    const getTooltipContent = (item) => {
      return getItemContent(item);
    };
    
    // 말풍선 상태 관리
    const tooltip = ref({
      visible: false,
      content: '',
      style: {
        left: '0px',
        top: '0px'
      }
    });
    
    // 말풍선 표시
    const showTooltip = (event, item) => {
      const rect = event.target.getBoundingClientRect();
      tooltip.value = {
        visible: true,
        content: getTooltipContent(item),
        style: {
          left: `${rect.left}px`,
          top: `${rect.bottom + 5}px`,
          position: 'fixed',
          zIndex: 1000
        }
      };
    };
    
    // 말풍선 숨기기
    const hideTooltip = () => {
      tooltip.value.visible = false;
    };
    
    const salesUnits = [
      { value: 'da', label: '스페셜 DA' },
      { value: 'timeboard', label: '타임보드' }
    ];
    
    // Define week options as a constant array
    const weekOptions = [0, 1, 2, 3, 4, 5];
    
    // 주차 라벨 표시 함수
    const getWeekLabel = (weekNum) => {
      switch(weekNum) {
        case 0: return '이번 주';
        case 1: return '1주 후';
        case 2: return '2주 후';
        case 3: return '3주 후';
        case 4: return '4주 후';
        case 5: return '5주 후';
        default: return `${weekNum}주 후`;
      }
    };
    
    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      fetchInventoryData();
    });
    
    return {
      inventoryData,
      loading,
      error,
      salesUnit,
      week,
      weekDays,
      weekOptions, // Add weekOptions to the return object
      formatHour,
      fetchInventoryData,
      getInventoryItemsForTimeSlot,
      getItemStyle,
      getItemContent,
      rawData,
      showRawData,
      toggleRawData,
      tooltip,
      showTooltip,
      hideTooltip,
      salesUnits,
      isToday,
      getWeekLabel
    };
  }
};
</script>

<style scoped>
.nosp-inventory-container {
  padding: 5px 20px;
  font-family: 'Noto Sans KR', sans-serif;
  max-width: 1650px;
  margin: 0 auto;
  width: 100%;
}

@media (max-width: 1650px) {
  .nosp-inventory-container {
    padding: 5px 10px;
  }
}

@media (max-width: 1200px) {
  .nosp-inventory-container {
    padding: 5px 5px;
  }
}

h1 {
  color: #333;
  margin-bottom: 10px;
  font-size: 20px;
  font-weight: 600;
}

.filter-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 5px;
}

.sales-unit-tabs {
  display: flex;
  gap: 10px;
}

.tab-button {
  padding: 10px 20px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
}

.tab-button:hover {
  background-color: #f5f5f5;
}

.tab-button.active {
  color: #007bff;
  background-color: #e6f2ff;
  font-weight: 600;
}

.week-selector {
  display: flex;
  align-items: center;
}

.week-buttons {
  display: flex;
  gap: 10px;
  background-color: #f5f5f5;
  padding: 5px;
  border-radius: 6px;
}

.week-button {
  padding: 8px 16px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
}

.week-button:hover {
  background-color: #fff;
}

.week-button.active {
  background-color: #fff;
  color: #007bff;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.timetable {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
  overflow: hidden;
}

.timetable th, .timetable td {
  border: 1px solid #e0e0e0;
  text-align: center;
  min-width: 120px;
}

.timetable th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 0;
  height: 25px;
  font-weight: 600;
  color: #333;
  vertical-align: middle;
  border: 1px solid #e0e0e0;
}

.timetable td:first-child {
  background-color: #f8f9fa;
  position: sticky;
  left: 0;
  z-index: 5;
  min-width: 60px;
  font-weight: 500;
  height: 22px;
  vertical-align: middle;
}

.inventory-item {
  position: relative;
  padding: 2px;
  font-size: 12px;
  border-radius: 4px;
  margin: 1px;
  text-align: left;
  overflow: hidden;
  word-break: break-word;
  height: 20px;
  cursor: pointer;
  background-color: #5b9bd5;
  color: white;
  transition: background-color 0.3s ease;
}

.inventory-item:hover {
  background-color: #4a8bc4;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #d9534f;
  padding: 15px;
  border: 1px solid #d9534f;
  border-radius: 4px;
  background-color: #f9f2f2;
}

.timetable-container {
  overflow-x: auto;
}

.time-slot {
  position: relative;
  height: 22px;
  vertical-align: top;
  padding: 0;
}

.all-day-row {
  height: 30px;
}

/* 디버깅 영역 스타일 */
.debug-container {
  margin-top: 30px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.debug-container h3 {
  margin-top: 0;
  display: inline-block;
  margin-right: 15px;
}

.toggle-button {
  display: inline-block;
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.toggle-button:hover {
  background-color: #0069d9;
}

.raw-data {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  font-family: monospace;
  white-space: pre-wrap;
}

.tooltip {
  background-color: #333;
  color: white;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-line;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  pointer-events: none;
}

.today-column {
  background-color: antiquewhite;
}
</style>